import { useEffect } from "react";
import { useQuery as defaultUseQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { ApolloInjection, QueryResult } from "@zapier/checkout/esm/types";
import { invoicePreviewQuery as InvoicePreviewQueryData } from "@zapier/checkout/esm/graphql-types";

type Input = {
  accountId: number;
  planId: string | number | null | undefined;
  currency: string | null | undefined;
  paymentSourceId: string | null | undefined;
} & ApolloInjection;

export const invoicePreviewQuery = gql`
  query invoicePreviewQuery($accountId: Int!, $planId: ID, $currency: String) {
    account(id: $accountId) {
      id
      invoicePreview(planId: $planId, currency: $currency) {
        amountDue
        currency
        id
        invoicedAt
        items {
          amount
          currency
          description
          id
        }
        periodEnd
        startingBalance
        total
        paymentIntent {
          id
          clientSecret
          processorPaymentSourceId
        }
        metadata {
          terms
        }
      }
    }
  }
`;

export const useInvoicePreview = ({
  accountId,
  planId,
  currency,
  paymentSourceId,
  useQuery = defaultUseQuery,
}: Input) => {
  const shouldSkip = !accountId || !planId;

  const queryResults: QueryResult<InvoicePreviewQueryData> = useQuery(
    invoicePreviewQuery,
    {
      fetchPolicy: "network-only",
      skip: shouldSkip,
      variables: {
        accountId,
        planId,
        currency,
      },
    }
  );

  const { refetch } = queryResults;

  /**
   * Regenerate a payment intent ID if the payment source changes
   */
  useEffect(() => {
    if (paymentSourceId && !shouldSkip) {
      refetch();
    }
  }, [paymentSourceId, shouldSkip, refetch]);

  return {
    error: queryResults.error
      ? "There was an issue loading your invoice preview. Please try again"
      : null,
    invoice: queryResults?.data?.account?.invoicePreview,
    isLoading: queryResults.loading,
  };
};
